import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../Home/components/Navbar';
import { Footer } from '../Home/components/Footer';
import { eventService } from '../../utils/api/event.service';
import './OrganizerProfile.css';

export const OrganizerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('events');

  useEffect(() => {
    const fetchOrganizerData = async () => {
      try {
        setLoading(true);
        const data = await eventService.getOrganizerProfile(id);
        
        if (data.result) {
          setOrganizer(data.result.organizer);
          setEvents(data.result.events || []);
        } else {
          setError(data.message || 'Failed to fetch organizer profile');
        }
      } catch (err) {
        console.error('Error fetching organizer profile:', err);
        setError(err.message || 'An error occurred while fetching organizer profile');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrganizerData();
    }

    if (window.Webflow) {
      window.Webflow.destroy();
      window.Webflow.ready();
      window.Webflow.require('ix2').init();
    }
    window.scrollTo(0, 0);
  }, [id]);

  const handleEventClick = (eventId) => {
    navigate(`/event-details/${eventId}`);
  };

  if (loading) {
    return (
      <div className="organizer-profile-wrapper">
        <Navbar dark={false} />
        <div className="loading-container">
          <div className="organizer-loader"></div>
          <p>Loading organizer profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organizer-profile-wrapper">
        <Navbar dark={false} />
        <div className="error-container">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="organizer-profile-wrapper hero-bg-listing-creative">
      <Navbar dark={true} />
      
      {/* Gradient Banner */}
      <div className="profile-banner"></div>
      
      <main className="organizer-main-content">
        {/* Profile Header Section */}
        <div className="profile-header-section">
          <div className="profile-left">
            <div className="profile-avatar-wrapper">
              <img 
                src={organizer?.profile_pic || '../../../public/images/default-avatar.png'} 
                alt={organizer?.full_name} 
                className="profile-avatar"
              />
            </div>
          </div>
          
          <div className="profile-center">
            <div className="profile-name-row">
              <h1 className="profile-name">{organizer?.full_name}</h1>
              <span className="organizer-badge">Organizer</span>
            </div>
            <p className="profile-tagline">
              {organizer?.bio || 'Event organizer passionate about creating memorable experiences'}
            </p>
            <div className="profile-actions">
              <button className="btn-follow">Follow</button>
              <button className="btn-contact">Get in touch</button>
            </div>
          </div>
          
          <div className="profile-stats">
            <div className="stat-block">
              <span className="stat-number">{events?.length || 0}</span>
              <span className="stat-label">Events</span>
            </div>
            <div className="stat-block">
              <span className="stat-number">{organizer?.followers || 0}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat-block">
              <span className="stat-number">{organizer?.rating || 'N/A'}</span>
              <span className="stat-label">Rating</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          <button 
            className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            Events <span className="tab-count">{events?.length || 0}</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
        </div>

        {/* Content */}
        {activeTab === 'events' && (
          <div className="events-section">
            {events.length > 0 ? (
              <div className="events-grid">
                {events.map((event) => (
                  <div 
                    key={event._id} 
                    className="event-card"
                    onClick={() => handleEventClick(event._id)}
                  >
                    <div className="event-card-image">
                      <img 
                        src={event?.banner_Image || '../..//images/default-event.jpg'} 
                        alt={event.title} 
                      />
                    </div>
                    <div className="event-card-footer">
                      <div className="event-info">
                        <h3 className="event-title">{event.title}</h3>
                        <span className="event-category">{event.category?.service_name || 'Event'}</span>
                      </div>
                      <div className="event-stats">
                        <span className="event-stat">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                          </svg>
                          {event.likes || 0}
                        </span>
                        <span className="event-stat">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                          {event.views || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-events">
                <p>No events found from this organizer yet.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'about' && (
          <div className="about-section">
            <div className="about-card">
              <h3>About</h3>
              <p>{organizer?.bio || 'This organizer hasn\'t added a bio yet. Stay tuned for more information!'}</p>
            </div>
            
            <div className="about-card">
              <h3>Contact Information</h3>
              <div className="contact-list">
                {organizer?.email && (
                  <div className="contact-row">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    <span>{organizer.email}</span>
                  </div>
                )}
                {organizer?.phone && (
                  <div className="contact-row">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    <span>{organizer.phone}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="about-card">
              <h3>Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Age</span>
                  <span className="detail-value">{organizer?.age || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Gender</span>
                  <span className="detail-value">{organizer?.gender || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
